/**
 * This file exports a `ClientConfig` object that provides configuration
 * options used by the client. These values are sourced from cookies if
 * present, otherwise from environment variables.
 *
 * The `ClientConfiguration` interface defines the properties that can be
 * set in the configuration object.
 *
 * The `getCookie` function is used internally to retrieve the config object
 * from a cookie, and then remove the cookie from the browser once it's been
 * read. This is done to prevent the cookie from being sent to the server.
 *
 * @remarks
 * - the cookie is populated from a server-side cookie in `server.ts`.
 */
import type z from 'zod';
import { generateErrorMessage } from 'zod-error';
import { getCookie } from '#/client-config/getCookie';
import configSchema from '#/schemas/config';
import envSchema, { removePrefix } from '#/schemas/env';

export type ClientConfiguration = z.infer<typeof configSchema>;

export const ClientConfig: ClientConfiguration = loadClientConfig();

function loadClientConfig(): ClientConfiguration {
  let config: ClientConfiguration;

  if (import.meta.env.DEV) {
    const env = envSchema.parse(removePrefix(import.meta.env));
    config = {
      logoutUrl: env.LOGOUT_URL,
      loginUrl: env.LOGIN_URL,
      authStatusUrl: env.AUTH_STATUS_URL,
      subscriptionKey: env.SUBSCRIPTION_KEY,
      apiUrl: env.API_URL,
      applicationInsightsConnectionString:
        env.APPLICATIONINSIGHTS_CONNECTION_STRING,
    };
  } else {
    const cookieConfig = getCookie('clientConfig');
    if (cookieConfig) {
      config = cookieConfig;
    } else {
      throw new Error('No client config found in cookie');
    }
  }

  const validation = configSchema.safeParse(config);
  if (!validation.success) {
    console.error('Invalid config');

    console.warn(
      generateErrorMessage(validation.error.issues, {
        delimiter: { error: '\n' },
      }),
    );
  }

  return config;
}
